import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import SharedPhysicalAppearanceSection from '@/components/shared/SharedPhysicalAppearanceSection';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface EditableAppearanceSectionProps {
  formData: any;
  updateLocalData: (field: string, value: any) => void;
  syncWithAuthContext: (updatedFields: Record<string, any>) => Promise<void>;
  userId: string;
}

const EditableAppearanceSection: React.FC<EditableAppearanceSectionProps> = ({
  formData,
  updateLocalData,
  syncWithAuthContext,
  userId
}) => {
  const [isSaving, setIsSaving] = useState<string | null>(null);

  const appearanceData = {
    altura_categoria: formData.height,
    tipo_corpo: formData.body_type,
    cor_cabelo: formData.hair_color,
    cor_olhos: formData.eye_color
  };

  const handleUpdateData = async (field: keyof typeof appearanceData, value: string) => {
    const fieldMapping: Record<string, string> = {
      'altura_categoria': 'height',
      'tipo_corpo': 'body_type', 
      'cor_cabelo': 'hair_color',
      'cor_olhos': 'eye_color'
    };

    const backendField = fieldMapping[field];
    
    // Update local state immediately
    updateLocalData(backendField, value);
    
    // Auto-save to database
    if (!userId) return;
    
    setIsSaving(backendField);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ [backendField]: value })
        .eq('id', userId);

      if (error) throw error;

      await syncWithAuthContext({ [backendField]: value });
      toast.success('Salvo', { duration: 1500 });
    } catch (error) {
      console.error('Error updating appearance:', error);
      toast.error('Erro ao salvar');
    } finally {
      setIsSaving(null);
    }
  };

  return (
    <Card className="bg-white/5 border-white/10">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center">
              <span className="text-lg">👤</span>
            </div>
            <h3 className="text-white font-medium text-lg">Aparência Física</h3>
          </div>
          {isSaving && (
            <span className="text-xs text-white/40">Salvando...</span>
          )}
        </div>

        <SharedPhysicalAppearanceSection
          data={appearanceData}
          updateData={handleUpdateData}
          isOnboarding={false}
        />
      </CardContent>
    </Card>
  );
};

export default EditableAppearanceSection;
